import { useState, useEffect } from 'react';
import { Cookie, Shield, BarChart3, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useCookieConsent } from '@/hooks/useCookieConsent';

interface CookiePreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const COOKIE_CATEGORIES = [
  {
    id: 'essential',
    name: 'Essential',
    description: 'Required for basic functionality',
    icon: Shield,
    alwaysOn: true,
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us improve Neeli',
    icon: BarChart3,
    alwaysOn: false,
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Personalized recommendations',
    icon: Megaphone,
    alwaysOn: false,
  },
];

export function CookiePreferencesDialog({ open, onOpenChange }: CookiePreferencesDialogProps) {
  const { preferences, setPreferences } = useCookieConsent();
  
  const [analyticsEnabled, setAnalyticsEnabled] = useState(preferences?.analytics ?? false);
  const [marketingEnabled, setMarketingEnabled] = useState(preferences?.marketing ?? false);

  // Sync with current preferences when dialog opens
  useEffect(() => {
    if (open && preferences) {
      setAnalyticsEnabled(preferences.analytics);
      setMarketingEnabled(preferences.marketing);
    }
  }, [open, preferences]);

  const handleSave = () => {
    setPreferences({
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
    });
    onOpenChange(false);
  };

  const getToggleState = (categoryId: string): boolean => {
    if (categoryId === 'essential') return true;
    if (categoryId === 'analytics') return analyticsEnabled;
    if (categoryId === 'marketing') return marketingEnabled;
    return false;
  };

  const handleToggle = (categoryId: string, enabled: boolean) => {
    if (categoryId === 'analytics') setAnalyticsEnabled(enabled);
    if (categoryId === 'marketing') setMarketingEnabled(enabled);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cookie className="h-5 w-5 text-primary" />
            Cookie Preferences
          </DialogTitle>
          <DialogDescription>
            Manage how we use cookies on this site.{' '}
            <Link to="/privacy" className="text-primary hover:underline">
              Privacy Policy
            </Link>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {COOKIE_CATEGORIES.map((category) => {
            const Icon = category.icon;
            const isEnabled = getToggleState(category.id);
            
            return (
              <div 
                key={category.id}
                className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                  isEnabled 
                    ? 'border-primary/30 bg-primary/5' 
                    : 'border-border'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
                    <Icon className={`h-4 w-4 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <Label className="font-medium">{category.name}</Label>
                    <p className="text-xs text-muted-foreground">{category.description}</p>
                  </div>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(checked) => handleToggle(category.id, checked)}
                  disabled={category.alwaysOn}
                  className={category.alwaysOn ? 'opacity-50' : ''}
                />
              </div>
            );
          })}
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} className="flex-1">
            Save Preferences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
